import { useFocusEffect } from '@react-navigation/native';
import { useFocusEffect as useExpoRouterFocusEffect } from 'expo-router';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A safer alternative to `useFocusEffect` from `@react-navigation/native`
 * that works in both react-navigation and expo-router contexts.
 *
 * When rendered inside a `NavigationContainer`, delegates to
 * `useFocusEffect` from `@react-navigation/native`.
 *
 * When rendered outside a `NavigationContainer` (e.g. expo-router screens),
 * falls back to `useFocusEffect` from `expo-router`.
 *
 * When neither context is available (e.g. in tests), silently no-ops.
 */
export const useSafeFocusEffect = (effect: EffectCallback) => {
  try {
    useFocusEffect(effect);
  } catch {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useExpoRouterFocusEffect(effect);
    } catch {
      // No navigation context available (e.g. in tests). No-op.
    }
  }
};

// ─── Types ───────────────────────────────────────────────────────────────────

type EffectCallback = () => undefined | void | (() => void);
